import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { MapPin } from "lucide-react"
import Spinner from "react-bootstrap/Spinner"
import { useAdminToolbarPopoutPosition } from "../hooks/useAdminToolbarPopoutPosition.js"
import { ADMIN_TOOLBAR_MENU_MAX_HEIGHT } from "../helpers/adminToolbarMenuConstants.js"

const MIN_LOCATION_SWITCH_MS = 1000
const LOCATION_MENU_MAX_HEIGHT = ADMIN_TOOLBAR_MENU_MAX_HEIGHT

function scrollSelectedLocationIntoView(list, selected) {
    if (!list || !selected) {
        return false
    }

    const listRect = list.getBoundingClientRect()
    const selectedRect = selected.getBoundingClientRect()

    if (selectedRect.top < listRect.top) {
        list.scrollTop -= listRect.top - selectedRect.top
    } else if (selectedRect.bottom > listRect.bottom) {
        list.scrollTop += selectedRect.bottom - listRect.bottom
    }

    return true
}

function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * @param {{
 *   locations: Array<{ id?: number | string, lodge_code?: string, name?: string }>,
 *   value: number | null | undefined,
 *   onChange: (locationId: number) => Promise<void>,
 *   disabled?: boolean,
 *   isOpen?: boolean,
 *   onOpenChange?: (open: boolean) => void,
 *   toolbarBg?: string,
 * }} props
 */
export default function AdminLocationPicker({
    locations,
    value,
    onChange,
    disabled = false,
    isOpen = false,
    onOpenChange,
    toolbarBg = "#0f6170",
}) {
    const [pendingLocationId, setPendingLocationId] = useState(null)
    const selectedOptionRef = useRef(null)
    const listRef = useRef(null)
    const didInitialScrollRef = useRef(false)
    const { triggerRef, menuRef, menuStyle } = useAdminToolbarPopoutPosition(isOpen, {
        maxHeightCap: LOCATION_MENU_MAX_HEIGHT,
    })
    const loading = pendingLocationId !== null

    const options = useMemo(
        () =>
            [...(locations ?? [])]
                .filter((l) => l?.lodge_code)
                .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })),
        [locations]
    )

    const current = useMemo(
        () => options.find((l) => Number(l.id) === Number(value)) ?? null,
        [options, value]
    )

    useEffect(() => {
        if (!isOpen) {
            didInitialScrollRef.current = false
            return
        }
        if (loading || didInitialScrollRef.current) {
            return
        }

        function runScroll() {
            if (scrollSelectedLocationIntoView(listRef.current, selectedOptionRef.current)) {
                didInitialScrollRef.current = true
            }
        }

        let innerRafId = 0
        const outerRafId = window.requestAnimationFrame(() => {
            runScroll()
            innerRafId = window.requestAnimationFrame(runScroll)
        })

        return () => {
            window.cancelAnimationFrame(outerRafId)
            window.cancelAnimationFrame(innerRafId)
        }
    }, [isOpen, loading])

    function setIsOpen(open) {
        onOpenChange?.(open)
    }

    async function handleSelect(locationId) {
        if (loading || disabled || Number(locationId) === Number(value)) {
            return
        }

        setPendingLocationId(locationId)
        try {
            await Promise.all([onChange(locationId), wait(MIN_LOCATION_SWITCH_MS)])
            setIsOpen(false)
        } catch (e) {
            console.error("Failed to change location:", e)
        } finally {
            setPendingLocationId(null)
        }
    }

    if (!options.length) {
        return null
    }

    const lodgeCode = current?.lodge_code ?? "—"

    return (
        <div className="lodge-admin-toolbar-cell lodge-admin-toolbar-cell--control lodge-admin-location-picker">
            <button
                type="button"
                ref={triggerRef}
                className={
                    "lodge-admin-toolbar-cell-btn lodge-admin-location-picker-trigger" +
                    (loading ? " lodge-admin-location-picker-trigger--loading" : "")
                }
                onClick={(e) => {
                    e.stopPropagation()
                    setIsOpen(!isOpen)
                }}
                disabled={disabled || loading}
                aria-expanded={isOpen}
                aria-busy={loading}
                aria-haspopup="listbox"
                aria-label={
                    loading ? "Switching location…" : `Current lodge: ${lodgeCode}. Select location`
                }
            >
                {loading ? (
                    <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Switching location…</span>
                    </Spinner>
                ) : (
                    <>
                        <span className="lodge-admin-location-picker-trigger-code">{lodgeCode}</span>
                        <MapPin size={16} className="lodge-admin-location-picker-trigger-icon" aria-hidden="true" />
                    </>
                )}
            </button>

            {isOpen
                ? createPortal(
                    <div
                        ref={menuRef}
                        className="lodge-admin-location-picker-menu lodge-admin-toolbar-popout-menu"
                        style={{
                            ...menuStyle,
                            background: toolbarBg,
                            "--lodge-admin-toolbar-bg": toolbarBg,
                        }}
                        role="listbox"
                        aria-label="Lodge locations"
                    >
                        <ul ref={listRef} className="lodge-admin-location-picker-list list-unstyled mb-0">
                            {options.map((location) => {
                                const id = Number(location.id)
                                const isSelected = id === Number(value)
                                const isPending = id === Number(pendingLocationId)
                                return (
                                    <li key={id}>
                                        <button
                                            type="button"
                                            role="option"
                                            ref={isSelected ? selectedOptionRef : undefined}
                                            aria-selected={isSelected}
                                            aria-busy={isPending}
                                            className={
                                                "lodge-admin-location-picker-option" +
                                                (isSelected ? " lodge-admin-location-picker-option--selected" : "") +
                                                (isPending ? " lodge-admin-location-picker-option--pending" : "")
                                            }
                                            disabled={loading || disabled}
                                            onClick={() => handleSelect(id)}
                                        >
                                            <span className="lodge-admin-location-picker-option-label">
                                                {location.name ?? location.lodge_code}
                                            </span>
                                            {isPending ? (
                                                <span className="lodge-admin-location-picker-option-spinner" aria-hidden="true">
                                                    <Spinner animation="border" size="sm" role="status" />
                                                    <span className="visually-hidden">Switching location…</span>
                                                </span>
                                            ) : null}
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>,
                    document.body
                )
                : null}
        </div>
    )
}
